import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSelector, useActions } from "app/utils/hooks";
import { RootState } from "typedefs";
import {
  fetchWorkflow,
  updateWorkflow,
  deleteWorkflow,
  clearWorkflow,
  updateWorkflowMembers,
  fetchWorkflowTemplate,
} from "app/actions/workflow";
import { UserModel } from "app/models";
import { cls } from "app/utils";
import { Spinner } from "app/components/Loading";
import Toast from "app/components/helpers/Toast";
import styles from "./styles.module.scss";
import WorkflowStep from "./_components/WorkflowStep";
import StepConfigPanel from "./_components/StepConfigPanel";
import QRCodeConfig from "./_components/QRCodeConfig";
import TermsConditionsConfig from "./_components/TermsConditionsConfig";
import RequiredPurchaseConfig from "./_components/RequiredPurchaseConfig";
import CreateSpaceConfig from "./_components/CreateSpaceConfig";
import AddToListConfig from "./_components/AddToListConfig";
import WorkflowPostsConfig from "./_components/WorkflowPostsConfig";
import AddMembersConfig from "./_components/AddMembersConfig";
import AssignPackageConfig from "./_components/AssignPackageConfig";
import WorkflowSettingsPanel from "./_components/WorkflowSettingsPanel";
import panelStyles from "./_components/StepConfigPanel/styles.module.scss";
import WorkflowArrow from "app/images/workflow-arrow.svg";

// Config fields to clear when a step toggle is turned OFF
const STEP_CLEAR_FIELDS: Record<string, string[]> = {
  createSpace: ["createSpaceSport"],
  addToList: ["addToListId"],
  assignOffering: ["assignOfferingId"],
};

// Reverse: config field → parent toggle to auto-enable on save
const CONFIG_TO_TOGGLE: Record<string, string> = {
  createSpaceSport: "createSpace",
  addToListId: "addToList",
  assignOfferingId: "assignOffering",
  termsDocumentIds: "acceptTerms",
};

// Toggle field → workflow field that must have content for the step to be enabled
const STEP_CONTENT_FIELDS: Record<string, string> = {
  acceptTerms: "termsDocuments",
  createSpace: "createSpaceSport",
  addToList: "addToListId",
  assignOffering: "assignOfferingId",
};

const STEP_DEFINITIONS = [
  {
    id: "qrCode",
    title: "Scan QR Code or Visit Link",
    subtitle: "QR code and Link set up",
    iconClass: "ico-qr-code",
    alwaysEnabled: true,
  },
  {
    id: "acceptTerms",
    title: "Accept Terms & Conditions",
    panelTitle: "Terms & Conditions",
    iconClass: "ico-feed-o",
    field: "acceptTerms",
  },
  // TODO: Unhide when backend adds require_purchase API fields
  // {
  //   id: "requirePurchase",
  //   title: "Require Purchase",
  //   panelTitle: "Required Purchase",
  //   iconClass: "ico-dollar-circle",
  //   field: "assignOffering",
  // },
  {
    id: "createSpace",
    title: "Create Space",
    iconClass: "ico-tspace",
    field: "createSpace",
  },
  {
    id: "addToList",
    title: "Add Space to List",
    panelTitle: "Add to List",
    iconClass: "ico-list",
    field: "addToList",
  },
  {
    id: "addPosts",
    title: "Add Posts to Space",
    panelTitle: "Workflow Posts",
    iconClass: "ico-feed-o",
    field: "addPosts",
  },
  {
    id: "addMembers",
    title: "Add Members to Space",
    panelTitle: "Add Members",
    iconClass: "ico-user-add-o",
    field: "addMembers",
  },
  {
    id: "assignPackage",
    title: "Assign a Package or Subscription to Athlete",
    panelTitle: "Package or Subscription",
    iconClass: "ico-star",
    field: "assignOffering",
  },
];

const WorkflowDetailPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: workflowId } = useParams<{ id: string }>();
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [toastSuccess, setToastSuccess] = useState<string | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);
  const [templateMembers, setTemplateMembers] = useState<UserModel[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  const { workflow, pending, error } = useSelector(
    (state: RootState) => ({
      workflow: state.workflow.selectedWorkflow,
      pending: state.workflow.pending,
      error: state.workflow.error,
    }),
    shallowEqual,
  );

  const workflowActions = useActions({
    fetchWorkflow,
    updateWorkflow,
    deleteWorkflow,
    clearWorkflow,
    updateWorkflowMembers,
    fetchWorkflowTemplate,
  });

  useEffect(() => {
    if (workflowId) {
      workflowActions.fetchWorkflow(workflowId);
    }
    return () => {
      workflowActions.clearWorkflow();
    };
  }, [workflowId]);

  useEffect(() => {
    if (activeStepId === "addMembers" && workflow?.spaceTemplateId && workflowId) {
      setMembersLoading(true);
      workflowActions
        .fetchWorkflowTemplate(workflow.spaceTemplateId, workflowId)
        .then((res: any) => {
          const members = (res?.members || []).map((m: any) => ({
            ...m,
            displayName:
              m.displayName ||
              [m.firstName, m.lastName].filter(Boolean).join(" ") ||
              m.email,
          }));
          setTemplateMembers(members);
        })
        .finally(() => setMembersLoading(false));
    } else {
      setTemplateMembers([]);
    }
  }, [activeStepId, workflow?.spaceTemplateId]);

  const steps = STEP_DEFINITIONS.map((def) => {
    let enabled = false;
    let subtitle = def.subtitle;

    if (def.alwaysEnabled) {
      enabled = true;
    } else if (workflow && def.field) {
      enabled = !!(workflow as any)[def.field];
    }

    // Dynamic subtitles from workflow data
    if (def.iconClass === "ico-tspace" && workflow?.createSpaceSport) {
      subtitle = `Sport: ${workflow.createSpaceSport}`;
    }

    return { ...def, enabled, subtitle };
  });

  const activeStep = steps.find((s) => s.id === activeStepId);

  const handleToggle = (stepId: string, enabled: boolean) => {
    const stepDef = STEP_DEFINITIONS.find((s) => s.id === stepId);
    if (!stepDef?.field || !workflowId) {
      return;
    }

    const apiField = stepDef.field;

    if (enabled) {
      // Check if the step has content; if not, open config panel instead
      const contentField = STEP_CONTENT_FIELDS[apiField];
      if (contentField) {
        const value = (workflow as any)?.[contentField];
        const hasContent = Array.isArray(value) ? value.length > 0 : !!value;
        if (hasContent) {
          workflowActions.updateWorkflow(workflowId, { [apiField]: true });
          return;
        }
        // Has content field but no content → open config panel
        handleStepClick(stepId);
      } else {
        // No content field to check (e.g. addPosts, addMembers) → enable and open panel
        workflowActions.updateWorkflow(workflowId, { [apiField]: true });
        handleStepClick(stepId);
      }
    } else {
      // When disabling, clear associated config fields
      const params: Record<string, any> = { [apiField]: false };
      const clearFields = STEP_CLEAR_FIELDS[apiField];
      if (clearFields) {
        clearFields.forEach((f) => {
          params[f] = null;
        });
      }
      workflowActions.updateWorkflow(workflowId, params);
    }
  };

  const handleStepClick = (stepId: string) => {
    setIsSettingsOpen(false);
    setActiveStepId(stepId);
  };

  const handleClosePanel = () => {
    setActiveStepId(null);
  };

  const handleSaveField = (field: string, value: any) => {
    if (!workflowId) {
      return;
    }

    const params: Record<string, any> = { [field]: value };

    // Auto-enable the parent toggle when saving config
    const toggleField = CONFIG_TO_TOGGLE[field];
    if (toggleField) {
      params[toggleField] = true;
    }

    workflowActions
      .updateWorkflow(workflowId, params)
      .then(() => setToastSuccess(t("Workflow updated")))
      .catch(() => setToastError(t("Failed to update workflow")));
  };

  const handleOpenSettings = () => {
    setActiveStepId(null);
    setIsSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setIsSettingsOpen(false);
  };

  const handleDeleteWorkflow = () => {
    if (workflowId) {
      workflowActions
        .deleteWorkflow(workflowId)
        .then(() => {
          history.push("/automation/facility-workflows");
        })
        .catch(() => setToastError(t("Failed to delete workflow")));
    }
  };

  const renderStepConfig = () => {
    if (!activeStep) {
      return null;
    }

    switch (activeStep.id) {
      case "qrCode":
        return (
          <QRCodeConfig
            qrUrl={workflow?.qrUrl}
            quickConnectUrl={
              workflow?.urlToken
                ? `https://app.coachnow.io/i/${workflow.urlToken}`
                : undefined
            }
          />
        );
      case "acceptTerms":
        return (
          <TermsConditionsConfig
            termsDocuments={workflow?.termsDocuments}
            onSave={(termsDocumentIds) =>
              handleSaveField("termsDocumentIds", termsDocumentIds)
            }
          />
        );
      case "requirePurchase":
        return (
          <RequiredPurchaseConfig
            selectedOfferingId={workflow?.assignOfferingId}
            onSelect={(offeringId) =>
              handleSaveField("assignOfferingId", offeringId)
            }
          />
        );
      case "createSpace":
        return (
          <CreateSpaceConfig
            selectedSport={workflow?.createSpaceSport}
            onSelect={(sport) => handleSaveField("createSpaceSport", sport)}
          />
        );
      case "addToList":
        return (
          <AddToListConfig
            selectedListId={workflow?.addToListId}
            onSelect={(listId) => handleSaveField("addToListId", listId)}
          />
        );
      case "addPosts":
        if (!workflow?.spaceTemplateId) {
          return (
            <div style={{ padding: 16, color: "#8c8c8c" }}>
              {t('Please enable the "Create Space" step first to add posts.')}
            </div>
          );
        }
        return (
          <WorkflowPostsConfig
            templateId={workflow.spaceTemplateId}
            onPostCreated={() => {
              if (workflowId && !workflow?.addPosts) {
                workflowActions.updateWorkflow(workflowId, {
                  addPosts: true,
                });
              }
            }}
          />
        );
      case "addMembers":
        return (
          <AddMembersConfig
            facilityId={workflow?.facilityId}
            initialMembers={templateMembers}
            loading={membersLoading}
            onSave={(memberIds) => {
              if (!workflowId) {
                return;
              }
              if (!workflow?.spaceTemplateId) {
                setToastError(
                  t(
                    'Please enable the "Create Space" step first to add members.',
                  ),
                );
                return;
              }
              workflowActions
                .updateWorkflowMembers(
                  workflow.spaceTemplateId,
                  workflowId,
                  memberIds,
                )
                .then(() => {
                  const shouldEnable = memberIds.length > 0;
                  if (workflow?.addMembers !== shouldEnable) {
                    workflowActions.updateWorkflow(workflowId, {
                      addMembers: shouldEnable,
                    });
                  }
                  setToastSuccess(t("Members updated"));
                })
                .catch(() => setToastError(t("Failed to update members")));
            }}
          />
        );
      case "assignPackage":
        return (
          <AssignPackageConfig
            selectedOfferingId={workflow?.assignOfferingId}
            onSelect={(offeringId) =>
              handleSaveField("assignOfferingId", offeringId)
            }
          />
        );
      default:
        return null;
    }
  };

  if (pending && !workflow) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (!pending && error && !workflow) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <p className={styles.error_text}>
            {t("Failed to load workflow. Please try again.")}
          </p>
          <Link
            to="/automation/facility-workflows"
            className={styles.breadcrumb_link}
          >
            {t("Back to Workflows")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <Link
            to="/automation/facility-workflows"
            className={styles.breadcrumb_link}
          >
            {t("Automation")}
          </Link>
          <span className={styles.breadcrumb_separator}>&gt;</span>
          <Link
            to="/automation/facility-workflows"
            className={styles.breadcrumb_link}
          >
            {t("Facility Workflows")}
          </Link>
          <span className={styles.breadcrumb_separator}>&gt;</span>
          <span className={styles.breadcrumb_current}>
            {workflow?.name || t("Workflow")}
          </span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.header_left}>
            <div className={styles.workflow_icon}>
              <i className="ico ico-workflow" />
            </div>
            <div className={styles.header_info}>
              <h1 className={styles.workflow_title}>
                {workflow?.name || t("Workflow")}
              </h1>
              <span className={styles.workflow_subtitle}>
                {t("Onboarding Workflow")}
              </span>
            </div>
          </div>
          <button className={styles.settings_btn} onClick={handleOpenSettings}>
            <i className="ico ico-cog" />
          </button>
        </div>

        {/* Flow Area */}
        <div className={cls(styles.flow_area, { [styles.flow_area_loading]: pending })}>
          <div className={styles.flow_content}>
            {/* Start Badge */}
            <div className={styles.flow_badge}>{t("Start")}</div>
            <img src={WorkflowArrow} alt="" className={styles.flow_arrow} />

            {/* Steps */}
            {steps.map((step) => (
              <React.Fragment key={step.id}>
                <WorkflowStep
                  title={t(step.title)}
                  subtitle={step.subtitle ? t(step.subtitle) : undefined}
                  iconClass={step.iconClass}
                  enabled={step.enabled}
                  onToggle={(enabled) => handleToggle(step.id, enabled)}
                  onStepClick={() => handleStepClick(step.id)}
                />
                <img src={WorkflowArrow} alt="" className={styles.flow_arrow} />
              </React.Fragment>
            ))}

            {/* End Badge */}
            <div className={styles.flow_badge}>{t("End")}</div>
          </div>
        </div>
      </div>

      {/* Step Config Panel */}
      <StepConfigPanel
        isOpen={!!activeStepId}
        onClose={handleClosePanel}
        title={activeStep ? t((activeStep as any).panelTitle || activeStep.title) : ""}
        panelClassName={
          activeStep?.id === "addPosts" ? panelStyles.panel_wide : undefined
        }
      >
        {renderStepConfig()}
      </StepConfigPanel>

      {/* Settings Panel */}
      <StepConfigPanel
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        title={t("Workflow Settings")}
      >
        {workflow && (
          <WorkflowSettingsPanel
            workflow={workflow}
            onSave={(params) => {
              if (workflowId) {
                workflowActions
                  .updateWorkflow(workflowId, params)
                  .then(() => setToastSuccess(t("Workflow updated")))
                  .catch(() => setToastError(t("Failed to update workflow")));
              }
            }}
            onDelete={handleDeleteWorkflow}
          />
        )}
      </StepConfigPanel>

      <Toast
        success={toastSuccess}
        error={toastError}
        onDone={() => {
          setToastSuccess(null);
          setToastError(null);
        }}
      />
    </div>
  );
};

export default WorkflowDetailPage;
